import Item from "@/entity/Item";
import ItemType from "@/entity/ItemType";
import { api } from "@/services/Api";
import { helper } from "@/services/Helper";

export interface ComparatorState {
  surveys: any;
  steps: any;
  sections: any;
  questions: any;
  questionsFiltered: any;
  choices: any;
  tags: any;
  suggestions: any;
  conditionGroups: any;
  conditions: any;
  mappings: any;
  responseGroups: any;
  responses: any;
  prospects: any;
  items: any;
  itemTypes: any;
  insurers: any;
  branches: any;
  autoCertificates: any;
  requiredCertificates: {
    yellow: number;
    blue: number;
    green: number;
  };
  currentResponseGroupUuid: string | null;
  currentSurveySlug: string | null;
  currentBranchSlug: string | null;
  lastSyncAt: string | null;
}

export const getDefaultState = (): ComparatorState => ({
  surveys: {},
  steps: {},
  sections: {},
  questions: {},
  questionsFiltered: {},
  choices: {},
  tags: {},
  suggestions: {},
  conditionGroups: {},
  conditions: {},
  mappings: {},
  responseGroups: {},
  responses: {},
  prospects: {},
  items: {},
  itemTypes: {},
  branches: {},
  insurers: {},
  autoCertificates: [],
  requiredCertificates: {
    yellow: 0,
    blue: 0,
    green: 0,
  },
  currentResponseGroupUuid: null,
  currentSurveySlug: null,
  currentBranchSlug: null,
  lastSyncAt: null,
});
const state: ComparatorState = getDefaultState();

export interface AddDataPayload {
  key: any;
  property: string;
  value: any;
}

const mutations = {
  REPLACE_OR_ADD_AUTO_CERTIFICATE(state: ComparatorState, updated: any) {
    const index = state.autoCertificates.findIndex(
      (item: any) => item.uuid === updated.uuid
    );
    if (index !== -1) {
      state.autoCertificates.splice(index, 1, updated);
    } else {
      state.autoCertificates.push(updated);
    }
  },
  SET_AUTO_CERTIFICATES(state: ComparatorState, payload: any) {
    state.autoCertificates = payload;
  },
  SET_ITEM_TYPES(state: ComparatorState, payload: any) {
    state.itemTypes = payload;
  },
  ADD_ITEM(state: ComparatorState, payload: Item) {
    state.items.push(payload);
  },
  ADD_DATA(state: ComparatorState, payload: AddDataPayload) {
    if (!state[payload.property]) {
      console.warn(payload.property + " key doesnt exist in comparatorState");
      return;
    }
    state[payload.property][payload.key] = payload.value;
  },
  SET_DATA(state: ComparatorState, payload: any) {
    if (!state[payload.property]) {
      console.warn(payload.property + " key doesnt exist in comparatorState");
      return;
    }
    if (helper.empty(state[payload.property])) {
      state[payload.property] = payload.value;
    } else {
      if (typeof payload.value === "object" && payload.value !== null) {
        Object.keys(payload.value).forEach((key) => {
          state[payload.property][key] = payload.value[key];
        });
      }
    }
  },
  REMOVE_ITEM(state: ComparatorState, uuid: string) {
    const index = state.items.findIndex((i: Item) => {
      return i.uuid === uuid;
    });
    if (index !== -1) {
      state.items.splice(index, 1);
    }
  },
  CLEAR_ITEMS(state: ComparatorState, payload: any) {
    state.items = [];
  },
  RESET(state: ComparatorState) {
    Object.assign(state, getDefaultState());
  },

  // Nouvelles mutations pour la structure plate
  SET_CURRENT_CONTEXT(
    state: ComparatorState,
    payload: { rgUuid?: string; surveySlug?: string; branchSlug?: string }
  ) {
    if (payload.rgUuid) state.currentResponseGroupUuid = payload.rgUuid;
    if (payload.surveySlug) state.currentSurveySlug = payload.surveySlug;
    if (payload.branchSlug) state.currentBranchSlug = payload.branchSlug;
  },

  // Stocke un survey complet (nested) en structure plate indexée par ID
  // Utilisé quand on reçoit un survey entier (pas via sync)
  STORE_SURVEY_FLAT(state: ComparatorState, survey: any) {
    if (!survey || !survey.slug) return;

    const surveyBase = { ...survey, steps: [] as number[] };

    if (survey.steps && Array.isArray(survey.steps)) {
      survey.steps.forEach((step: any) => {
        const stepId = step.id;
        surveyBase.steps.push(stepId);

        const stepBase = { ...step, sections: [] as number[] };

        if (step.sections && Array.isArray(step.sections)) {
          step.sections.forEach((section: any) => {
            const sectionId = section.id;
            stepBase.sections.push(sectionId);

            const sectionBase = { ...section, questions: [] as number[] };

            if (section.questions && Array.isArray(section.questions)) {
              section.questions.forEach((question: any) => {
                const questionId = question.id;
                sectionBase.questions.push(questionId);

                const questionBase = { ...question };

                // Stocker les choices par ID
                if (question.choices && Array.isArray(question.choices)) {
                  const choiceIds: number[] = [];
                  question.choices.forEach((choice: any) => {
                    const cId = choice.id;
                    choiceIds.push(cId);
                    state.choices[String(cId)] = { ...choice, question: questionId };
                  });
                  questionBase.choices = choiceIds;
                }

                // Stocker les conditionGroups
                if (question.conditionGroups && Array.isArray(question.conditionGroups)) {
                  const cgIds: number[] = [];
                  question.conditionGroups.forEach((cg: any) => {
                    const cgId = cg.id;
                    cgIds.push(cgId);
                    state.conditionGroups[String(cgId)] = { ...cg, question: questionId };
                    if (cg.conditions && Array.isArray(cg.conditions)) {
                      cg.conditions.forEach((c: any) => {
                        state.conditions[String(c.id)] = { ...c, group: cgId };
                      });
                    }
                  });
                  questionBase.conditionGroups = cgIds;
                }

                // Stocker les suggestions
                if (question.suggestions && Array.isArray(question.suggestions)) {
                  const sIds: number[] = [];
                  question.suggestions.forEach((s: any) => {
                    sIds.push(s.id);
                    state.suggestions[String(s.id)] = { ...s, question: questionId };
                  });
                  questionBase.suggestions = sIds;
                }

                // Stocker les tags
                if (question.tags && Array.isArray(question.tags)) {
                  question.tags.forEach((t: any) => {
                    if (t.id) state.tags[String(t.id)] = { ...t };
                  });
                }

                state.questions[String(questionId)] = questionBase;
              });
            }

            delete sectionBase.questions;
            sectionBase.questions = sectionBase.questions || [];
            state.sections[String(sectionId)] = sectionBase;
          });
        }

        delete stepBase.sections;
        stepBase.sections = stepBase.sections || [];
        state.steps[String(stepId)] = stepBase;
      });
    }

    delete surveyBase.steps;
    surveyBase.steps = surveyBase.steps || [];
    state.surveys[survey.slug] = surveyBase;
  },

  // Stocke un responseGroup et ses réponses
  STORE_RESPONSE_GROUP(state: ComparatorState, responseGroup: any) {
    if (!responseGroup || !responseGroup.uuid) return;

    const rgBase = {
      ...responseGroup,
      responseUuids: [] as string[],
    };

    // Stocker le prospect si présent
    if (responseGroup.prospect) {
      state.prospects[responseGroup.prospect.uuid] = responseGroup.prospect;
      rgBase.prospectUuid = responseGroup.prospect.uuid;
    }

    // Stocker les réponses séparément
    if (responseGroup.responses && Array.isArray(responseGroup.responses)) {
      responseGroup.responses.forEach((response: any) => {
        rgBase.responseUuids.push(response.uuid);

        // Extraire questionUuid depuis question.uuid si pas déjà défini
        const questionUuid =
          response.questionUuid ||
          (response.question && response.question.uuid) ||
          "";

        // Stocker la réponse avec référence à la question
        state.responses[response.uuid] = {
          ...response,
          responseGroupUuid: responseGroup.uuid,
          questionUuid: questionUuid,
        };

        // Si la question est incluse inline, la stocker aussi
        if (response.question && response.question.uuid) {
          if (!state.questions[response.question.uuid]) {
            state.questions[response.question.uuid] = response.question;
          }
        }
      });
    }

    // Supprimer les réponses et prospect imbriqués
    delete rgBase.responses;
    delete rgBase.prospect;

    state.responseGroups[responseGroup.uuid] = rgBase;
  },

  SET_LAST_SYNC_AT(state: ComparatorState, at: string | null) {
    state.lastSyncAt = at;
  },

  REMOVE_DATA(
    state: ComparatorState,
    payload: { property: string; key: string }
  ) {
    if (
      state[payload.property] &&
      state[payload.property][payload.key] !== undefined
    ) {
      delete state[payload.property][payload.key];
    }
  },

  // Met à jour une réponse (non utilisee actuellement, conservee pour usage futur)
  UPDATE_RESPONSE(
    state: ComparatorState,
    payload: { uuid: string; value: any; comment?: string }
  ) {
    if (state.responses[payload.uuid]) {
      state.responses[payload.uuid].value = payload.value;
      if (payload.comment !== undefined) {
        state.responses[payload.uuid].comment = payload.comment;
      }
    }
  },
};

const actions = {
  async loadAutoCertificates({ commit, state }: any) {
    api.get(api.core, "insurer/certificate/list").then((res: any) => {
      if (res && res.data && res.data.remaining) {
        commit("SET_AUTO_CERTIFICATES", res.data.remaining);
      }
    });
    return state.autoCertificates;
  },
  async loadItemTypes({ commit, state }: any, branchSlug: string) {
    const res = await api.get(api.core, "item/type/list/" + branchSlug);
    if (res && res.data && res.data.itemTypes) {
      const its: ItemType[] = [];
      res.data.itemTypes.forEach((it: any) => {
        const occ = new ItemType(it);
        its.push(occ);
      });
      commit("SET_ITEM_TYPES", its);
    }
    return state.autoCertificates;
  },

  // Synchronise la structure des formulaires via POST /api/sync
  // Reconstruit la structure plate avec les mêmes conventions que loadResponseGroupData :
  //   - surveys indexés par slug, steps → stepUuids
  //   - steps indexés par ID (string), sections → sectionUuids
  //   - sections indexés par ID (string), questions → questionUuids
  //   - questions indexés par ID (string), choiceUuids reconstitués
  //   - conditionGroups/conditions/suggestions avec cross-refs questionUuid/conditionGroupUuid
  // Supporte le delta sync via le timestamp "since"
  async syncFormStructure(
    { commit, state }: any,
    payload?: { branches?: string[] }
  ) {
    const body: any = {
      scope: [
        "surveys",
        "steps",
        "sections",
        "questions",
        "choices",
        "conditionGroups",
        "conditions",
        "mappings",
        "suggestions",
        "tags",
      ],
    };

    if (payload && payload.branches && payload.branches.length > 0) {
      body.branches = payload.branches;
    }

    // Delta sync si on a déjà synchronisé
    if (state.lastSyncAt) {
      body.since = state.lastSyncAt;
    }

    const res = await api.post(api.form, "api/sync", body, {
      headers: { Source: "comparateur" },
    });

    if (!res || !res.data || res.data.status !== "success") {
      console.error("Sync failed:", res?.data);
      return { success: false, error: "Sync failed" };
    }

    const syncState = res.data.state;

    // Helper pour traiter les suppressions
    const processDeleted = (entity: string, deleted: any[]) => {
      if (deleted && Array.isArray(deleted)) {
        deleted.forEach((id: any) => {
          // Pour les surveys stockés par slug, chercher par id
          if (entity === "surveys") {
            Object.keys(state.surveys).forEach((key: string) => {
              if (
                state.surveys[key] &&
                String(state.surveys[key].id) === String(id)
              ) {
                commit("REMOVE_DATA", { property: "surveys", key });
              }
            });
          } else {
            commit("REMOVE_DATA", { property: entity, key: String(id) });
          }
        });
      }
    };

    // --- 1. Stocker d'abord les entités "feuilles" (sans dépendances descendantes) ---

    // Choices (indexés par ID string)
    if (syncState.choices) {
      if (syncState.choices.updates) {
        Object.keys(syncState.choices.updates).forEach((id: string) => {
          const choice = { ...syncState.choices.updates[id] };
          // choice.question = ID numérique de la question parente
          choice.questionUuid = String(choice.question);
          state.choices[id] = choice;
        });
      }
      processDeleted("choices", syncState.choices.deleted);
    }

    // Conditions (indexés par ID string)
    if (syncState.conditions) {
      if (syncState.conditions.updates) {
        Object.keys(syncState.conditions.updates).forEach((id: string) => {
          const condition = { ...syncState.conditions.updates[id] };
          // conditionGroupUuid sera défini après via les conditionGroups
          state.conditions[id] = condition;
        });
      }
      processDeleted("conditions", syncState.conditions.deleted);
    }

    // ConditionGroups (indexés par ID string)
    if (syncState.conditionGroups) {
      if (syncState.conditionGroups.updates) {
        Object.keys(syncState.conditionGroups.updates).forEach((id: string) => {
          const cg = { ...syncState.conditionGroups.updates[id] };
          // cg.question = ID numérique de la question ciblée
          cg.questionUuid = String(cg.question);
          cg.uuid = id;
          state.conditionGroups[id] = cg;

          // Cross-ref : marquer chaque condition avec conditionGroupUuid
          if (cg.conditions && Array.isArray(cg.conditions)) {
            cg.conditions.forEach((cId: any) => {
              const cKey = String(cId);
              if (state.conditions[cKey]) {
                state.conditions[cKey].conditionGroupUuid = id;
                state.conditions[cKey].uuid = cKey;
              }
            });
          }
        });
      }
      processDeleted("conditionGroups", syncState.conditionGroups.deleted);
    }

    // Suggestions (indexés par ID string)
    if (syncState.suggestions) {
      if (syncState.suggestions.updates) {
        Object.keys(syncState.suggestions.updates).forEach((id: string) => {
          const suggestion = { ...syncState.suggestions.updates[id] };
          // suggestion.question = ID numérique de la question parente
          suggestion.questionUuid = String(suggestion.question);
          state.suggestions[id] = suggestion;
        });
      }
      processDeleted("suggestions", syncState.suggestions.deleted);
    }

    // Mappings (indexés par ID string)
    if (syncState.mappings) {
      if (syncState.mappings.updates) {
        Object.keys(syncState.mappings.updates).forEach((id: string) => {
          state.mappings[id] = syncState.mappings.updates[id];
        });
      }
      processDeleted("mappings", syncState.mappings.deleted);
    }

    // Tags (indexés par ID string)
    if (syncState.tags) {
      if (syncState.tags.updates) {
        Object.keys(syncState.tags.updates).forEach((id: string) => {
          state.tags[id] = syncState.tags.updates[id];
        });
      }
      processDeleted("tags", syncState.tags.deleted);
    }

    // --- 2. Questions (indexées par ID string) ---
    if (syncState.questions) {
      if (syncState.questions.updates) {
        Object.keys(syncState.questions.updates).forEach((id: string) => {
          const question = { ...syncState.questions.updates[id] };

          // Reconstruire choices[] depuis les choices qui référencent cette question
          if (!question.choices || !Array.isArray(question.choices) || question.choices.length === 0) {
            question.choices = [] as number[];
            Object.keys(state.choices).forEach((choiceKey: string) => {
              const c = state.choices[choiceKey];
              if (String(c.question) === id) {
                question.choices.push(Number(choiceKey));
              }
            });
          }

          state.questions[id] = question;
        });
      }
      processDeleted("questions", syncState.questions.deleted);
    }

    // --- 3. Sections (indexées par ID string) ---
    if (syncState.sections) {
      if (syncState.sections.updates) {
        Object.keys(syncState.sections.updates).forEach((id: string) => {
          state.sections[id] = { ...syncState.sections.updates[id] };
        });
      }
      processDeleted("sections", syncState.sections.deleted);
    }

    // --- 4. Steps (indexés par ID string) ---
    if (syncState.steps) {
      if (syncState.steps.updates) {
        Object.keys(syncState.steps.updates).forEach((id: string) => {
          state.steps[id] = { ...syncState.steps.updates[id] };
        });
      }
      processDeleted("steps", syncState.steps.deleted);
    }

    // --- 5. Surveys (indexés par slug) ---
    if (syncState.surveys) {
      if (syncState.surveys.updates) {
        Object.keys(syncState.surveys.updates).forEach((id: string) => {
          const survey = { ...syncState.surveys.updates[id] };
          const key = survey.slug || id;
          state.surveys[key] = survey;
        });
      }
      processDeleted("surveys", syncState.surveys.deleted);
    }

    // Sauvegarder le timestamp pour le prochain delta sync
    commit("SET_LAST_SYNC_AT", res.data.at);

    return { success: true, at: res.data.at };
  },

  // Charge toutes les données pour un responseGroup via GET /api/get/new/data/{uuid}
  // Les deux APIs (sync et get/new/data) indexent désormais par ID numérique.
  async loadResponseGroupData(
    { commit, state, getters, dispatch }: any,
    rgUuid: string
  ) {
    // get/new/data renvoie déjà la structure du formulaire — pas besoin de sync séparé

    // Charger les données du ResponseGroup
    const rgRes = await api.get(api.form, "api/get/new/data/" + rgUuid);

    if (!rgRes || !rgRes.data) {
      return { success: false, error: "No response from API" };
    }

    const data = rgRes.data;

    if (data.status !== "success" || !data.responseGroup) {
      console.error("ResponseGroup not found in response:", data);
      return { success: false, error: "ResponseGroup not found" };
    }

    // --- 1. Merger la structure du formulaire (indexée par ID, même format que sync) ---

    const mergeEntity = (entity: string, source: any) => {
      if (!source) return;
      Object.keys(source).forEach((key: string) => {
        state[entity][key] = { ...(state[entity][key] || {}), ...source[key] };
      });
    };

    // Choices, conditionGroups, conditions, suggestions, tags, mappings (feuilles)
    mergeEntity("choices", data.choices);
    mergeEntity("conditionGroups", data.conditionGroups);
    mergeEntity("conditions", data.conditions);
    mergeEntity("suggestions", data.suggestions);
    mergeEntity("tags", data.tags);
    mergeEntity("mappings", data.mappings);

    // Questions (indexées par ID) — merger et renommer steps/sections/questions en listes d'IDs
    if (data.questions) {
      Object.keys(data.questions).forEach((id: string) => {
        const question = { ...data.questions[id] };
        // choices/conditionGroups/suggestions/tags sont déjà des tableaux d'IDs depuis simplify()
        state.questions[id] = { ...(state.questions[id] || {}), ...question };
      });
    }

    // Sections (indexées par ID)
    if (data.sections) {
      Object.keys(data.sections).forEach((id: string) => {
        const section = { ...data.sections[id] };
        state.sections[id] = { ...(state.sections[id] || {}), ...section };
      });
    }

    // Steps (indexées par ID)
    if (data.steps) {
      Object.keys(data.steps).forEach((id: string) => {
        const step = { ...data.steps[id] };
        state.steps[id] = { ...(state.steps[id] || {}), ...step };
      });
    }

    // Survey (indexé par slug)
    if (data.survey) {
      const survey = { ...data.survey };
      // steps est déjà un tableau d'IDs depuis simplify()
      const key = survey.slug || String(survey.id);
      state.surveys[key] = { ...(state.surveys[key] || {}), ...survey };
    }

    if (data.questionsFiltered) {
      state.questionsFiltered = data.questionsFiltered;
    }

    // Charger les DataTypes depuis masterStore pour la résolution des types dans le getter
    await dispatch("masterStore/loadDataTypes", null, { root: true });

    // --- 2. Stocker le ResponseGroup et ses réponses ---

    const responseGroup = data.responseGroup;
    const rgBase = {
      ...responseGroup,
      responseUuids: [] as string[],
    };

    // Les réponses sont indexées par questionId dans responseGroup.responses
    if (responseGroup.responses) {
      Object.keys(responseGroup.responses).forEach((questionId: string) => {
        const response = responseGroup.responses[questionId];
        if (response.uuid) {
          rgBase.responseUuids.push(response.uuid);
          state.responses[response.uuid] = {
            ...response,
            responseGroupUuid: responseGroup.uuid,
            questionId: questionId,
          };
        }
      });
    }
    delete rgBase.responses;

    state.responseGroups[responseGroup.uuid] = rgBase;

    // --- 3. Extraire les slugs et contexte ---

    const surveySlug =
      data.responseGroup.surveySlug || (data.survey && data.survey.slug) || "";
    const branchSlug = data.branchSlug || data.responseGroup.branchSlug || "";

    // 4. Charger les données de la branch si nécessaire (insurers, etc.)
    if (branchSlug && !state.branches[branchSlug]) {
      const branchRes = await api.get(
        api.core,
        "api/get/comparator/data/" + branchSlug
      );
      if (branchRes && branchRes.data) {
        if (branchRes.data.branch) {
          commit("ADD_DATA", {
            property: "branches",
            key: branchSlug,
            value: branchRes.data.branch,
          });
        }
        if (branchRes.data.insurers) {
          const insurersArray = Array.isArray(branchRes.data.insurers)
            ? branchRes.data.insurers
            : Object.values(branchRes.data.insurers);

          insurersArray.forEach((insurer: any) => {
            commit("ADD_DATA", {
              property: "insurers",
              key: insurer.slug || insurer.id,
              value: insurer,
            });
          });
        }
      }
    }

    // 5. Définir le contexte courant
    commit("SET_CURRENT_CONTEXT", {
      rgUuid: responseGroup.uuid,
      surveySlug: surveySlug,
      branchSlug: branchSlug,
    });

    return {
      success: true,
      responseGroup: getters.getResponseGroupFull(responseGroup.uuid),
      survey: getters.getSurveyFull(surveySlug),
      branch: state.branches[branchSlug],
    };
  },
};

const getters = {
  loadAutoCertificates: (state: any, getters: any) => async () => {
    if (state.autoCertificates && !helper.empty(state.autoCertificates)) {
      return state.autoCertificates;
    }
    const res = await api.get(api.core, "insurer/certificate/list");
    if (res && res.data && res.data.remaining) {
      state.autoCertificates = res.data.remaining;
    }
    return state.autoCertificates;
  },
  getById: (state: any) => (property: string, id: string) => {
    if (!state[property]) return null;
    return state[property][id];
  },

  // Récupère un survey avec sa structure complète (steps/sections/questions)
  // Toutes les entités sont indexées par ID numérique (aligné sync + simplify)
  getSurveyFull:
    (state: ComparatorState, getters: any, rootState: any) =>
    (slug: string) => {
      const survey = state.surveys[slug];
      if (!survey) return null;

      // Reconstruire les steps
      const steps = (survey.steps || [])
        .map((stepId: number) => {
          const step = state.steps[String(stepId)];
          if (!step) return null;

          // Reconstruire les sections
          const sections = (step.sections || [])
            .map((sectionId: number) => {
              const section = state.sections[String(sectionId)];
              if (!section) return null;

              // Reconstruire les questions
              const questions = (section.questions || [])
                .map((questionId: number) => {
                  const qId = String(questionId);
                  const question = state.questions[qId];
                  if (!question) return null;

                  // Reconstruire les choices
                  const choices = (question.choices || [])
                    .map((choiceId: number) => state.choices[String(choiceId)])
                    .filter(Boolean);

                  // Reconstruire les conditionGroups avec leurs conditions
                  const conditionGroups = (question.conditionGroups || [])
                    .map((cgId: number) => {
                      const cg = state.conditionGroups[String(cgId)];
                      if (!cg) return null;
                      return {
                        ...cg,
                        conditions: (cg.conditions || [])
                          .map((cId: number) => state.conditions[String(cId)])
                          .filter(Boolean),
                      };
                    })
                    .filter(Boolean);

                  // Reconstruire les suggestions
                  const suggestions = (question.suggestions || [])
                    .map((sId: number) => state.suggestions[String(sId)])
                    .filter(Boolean);

                  // Résoudre le type numérique en objet DataType complet
                  let resolvedType = question.type;
                  if (
                    typeof resolvedType === "number" ||
                    (typeof resolvedType === "string" &&
                      !isNaN(Number(resolvedType)))
                  ) {
                    const dataTypes = rootState?.masterStore?.dataTypes || [];
                    const found = dataTypes.find(
                      (dt: any) => dt.id === Number(resolvedType)
                    );
                    resolvedType = found
                      ? { id: found.id, code: found.code, label: found.label, format: found.format, tags: found.tags || [] }
                      : { id: Number(resolvedType), code: "", label: "", format: "", tags: [] };
                  }

                  // Résoudre les tags
                  let tags: any[] = [];
                  if (question.tags && Array.isArray(question.tags)) {
                    tags = question.tags
                      .map((tagId: any) => {
                        if (typeof tagId === "object") return tagId;
                        return state.tags[String(tagId)] || null;
                      })
                      .filter(Boolean);
                  }

                  return {
                    ...question,
                    type: resolvedType,
                    choices,
                    conditionGroups,
                    suggestions,
                    tags,
                  };
                })
                .filter(Boolean);

              return { ...section, questions };
            })
            .filter(Boolean);

          return { ...step, sections };
        })
        .filter(Boolean);

      return { ...survey, steps };
    },

  // Récupère un responseGroup avec ses réponses
  getResponseGroupFull: (state: ComparatorState) => (uuid: string) => {
    const rg = state.responseGroups[uuid];
    if (!rg) return null;

    // Reconstruire les réponses
    const responses = (rg.responseUuids || [])
      .map((responseUuid: string) => {
        const response = state.responses[responseUuid];
        if (!response) return null;

        // Inclure la question si disponible (par ID numérique)
        const question = response.questionId
          ? state.questions[String(response.questionId)]
          : null;

        return {
          ...response,
          question,
        };
      })
      .filter(Boolean);

    // Inclure le prospect
    const prospect = rg.prospectUuid ? state.prospects[rg.prospectUuid] : null;

    return {
      ...rg,
      responses,
      prospect,
    };
  },

  // Récupère une réponse pour une question dans un responseGroup (par questionId numérique)
  getResponseForQuestion:
    (state: ComparatorState) => (rgUuid: string, questionId: string | number) => {
      const rg = state.responseGroups[rgUuid];
      if (!rg) return null;
      const qId = String(questionId);

      for (const responseUuid of rg.responseUuids || []) {
        const response = state.responses[responseUuid];
        if (response && String(response.questionId) === qId) {
          return response;
        }
      }
      return null;
    },

  // Récupère toutes les réponses d'un responseGroup indexées par questionId
  getResponsesByQuestion: (state: ComparatorState) => (rgUuid: string) => {
    const rg = state.responseGroups[rgUuid];
    if (!rg) return {};

    const responsesByQuestion: { [questionId: string]: any } = {};
    for (const responseUuid of rg.responseUuids || []) {
      const response = state.responses[responseUuid];
      if (response && response.questionId) {
        responsesByQuestion[String(response.questionId)] = response;
      }
    }
    return responsesByQuestion;
  },

  // Récupère le contexte courant
  currentContext: (state: ComparatorState) => {
    return {
      responseGroup: state.currentResponseGroupUuid
        ? state.responseGroups[state.currentResponseGroupUuid]
        : null,
      survey: state.currentSurveySlug
        ? state.surveys[state.currentSurveySlug]
        : null,
      branch: state.currentBranchSlug
        ? state.branches[state.currentBranchSlug]
        : null,
    };
  },

  // Récupère une question par ID
  getQuestion: (state: ComparatorState) => (id: string | number) => {
    return state.questions[String(id)] || null;
  },

  // Récupère les choices d'une question (par ID numérique)
  getChoicesForQuestion: (state: ComparatorState) => (questionId: string | number) => {
    const question = state.questions[String(questionId)];
    if (!question || !question.choices) return [];

    return question.choices
      .map((choiceId: number) => state.choices[String(choiceId)])
      .filter(Boolean);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
